define((require) => {
    "use strict";

    const {
        clone, omit, isEqual, extend, values
    }    = require("underscore");
    const Browser                                   = require("shared/services/Browser");
    const Color                                     = require("shared/util/Color");
    const DomHandler                                = require("ui/experience/widgets/textWidget/textEditor/DomHandler");
    const Preconditions                             = require("shared/util/Preconditions");
    const {dasherize}                               = require("shared/util/StringUtils");

    /**
     * TextEditorViewHelpers is a bundle of helper functions, mainly aimed for
     * helping out TextEditorView (and CKEditorFacade) with parsing strings,
     * pulling information out of the dom, etc.
     *
     * The helper functions are not entirely pure, in that they sometimes will
     * edit the elements being passed in as arguments (or children of them).
     */
    const TextEditorViewHelpers = {
        prepareFontFamily(changed, model, cssFamilyNames) {
            Preconditions.isObject(changed);
            Preconditions.isModel(model);
            Preconditions.isString(cssFamilyNames);

            let fontData = {};

            if (!changed.fontFamily) {
                return changed;
            }

            changed = clone(changed);

            fontData = {
                fontFamily: changed.fontFamily,
                cssFamilyNames: cssFamilyNames,
                fontProvider: changed.fontProvider || model.get("fontProvider")
            };

            if (changed.fontMetadata) {
                fontData.meta = changed.fontMetadata;
            }

            changed.fontFamily = fontData;

            return omit(changed, "fontProvider", "fontMetadata");
        },

        getCssValueFromComputedStyle(computedStyles, property) {
            Preconditions.isString(property);

            property = dasherize(property);

            let css = computedStyles[property];

            switch (property) {
                case "color": {
                    css = Color.rgbaToHex(css).hex;
                    break;
                }

                case "letter-spacing": {
                    css = css = parseInt(css, 10) || 0;
                    break;
                }

                case "font-size": {
                    css = parseInt(css, 10);
                    break;
                }

                case "line-height": {
                    const fontSize = parseInt(computedStyles["font-size"], 10);
                    // The return line height value is the actual line height times the font size
                    // We need to limit it to 1 decimal and remove any trailing zero.
                    css = parseFloat((css.replace("px", "") / fontSize).toFixed(1));
                    break;
                }

                case "text-decoration": {
                    css = computedStyles.textDecorationLine ? computedStyles.textDecorationLine : css;
                    break;
                }

                default: { // No need to log here.
                    break;
                }
            }

            return css;
        },

        getCssValuesFromElements(result, properties, computedStyles) {
            Preconditions.isObject(result);
            Preconditions.isArray(properties);

            computedStyles.forEach((computedStyle) => {
                properties.forEach((property) => {
                    const oldValue = result[property];
                    const newValue = TextEditorViewHelpers.getCssValueFromComputedStyle(computedStyle, property);
                    let setValue;

                    if (!result.hasOwnProperty(property)) {
                        // Initial value
                        setValue = newValue;
                    } else if (isEqual(oldValue, newValue)) {
                        // Don't change the value
                        setValue = oldValue;
                    } else {
                        // Different values, so it's set to null
                        setValue = null;
                    }

                    result[property] = setValue;
                });
            });

            return result;
        },

        /**
         * Parse the passed elements to extract all "Fonts" that are applied to the elements and its descendants,
         * The "Font" is an object that consist of:
         *
         * <pre>
         * {
         *      fontProvider    : String,
         *      fontFamily      : String,
         *      fontStyle       : String,
         *      fontWeight      : string,
         *      fontMetadata    : object
         *  }
         *  </pre>
         *
         * @param {HTMLElement[]}   elements
         * @returns {Object[]}      An array of font Objects
         */
        parseFont(elements, container) {
            Preconditions.isArray(elements);

            elements = Array.isArray(elements) ? elements : [elements];

            const textNodeParentList = DomHandler.getTextNodeParentList(elements);

            const allFonts = textNodeParentList.reduce((fonts, element) => {
                const computedStyles = Browser.computedStyle(element);
                const elementWithFontData = DomHandler.getParentWithFontFamily(element, container);
                const kitID = elementWithFontData.getAttribute("data-kitid");
                const projectId = elementWithFontData.getAttribute("data-projectid");
                const path = elementWithFontData.getAttribute("data-path");
                const customFontId = elementWithFontData.getAttribute("data-customfontid");
                const format = elementWithFontData.getAttribute("data-format");
                const fontMetadata = {};
                const fontStyle = computedStyles.fontStyle;
                const fontWeight = computedStyles.fontWeight;
                const fontFamily = elementWithFontData.getAttribute("data-font-family");
                const fontProvider = elementWithFontData.getAttribute("data-font-provider");
                const uid = fontProvider + fontFamily + fontStyle + fontWeight;

                if (kitID) {
                    fontMetadata.kitID = kitID;
                }

                if (projectId) {
                    fontMetadata.projectId = projectId;
                }

                if (path && customFontId && format) {
                    extend(fontMetadata, {
                        path,
                        customFontId,
                        format
                    });
                }

                if (fontFamily) {
                    fonts[uid] = fonts[uid] || {
                        fontProvider,
                        fontFamily,
                        fontStyle,
                        fontWeight,
                        fontMetadata
                    };
                }

                return fonts;
            }, {});

            return values(allFonts);
        },

        /**
         * Check how many Fonts applied to the passed elements, if there is only one Font, return it.
         * Otherwise, return empty Font.
         *
         * @param {HTMLElement[]} elements          The elements we are interested in
         * @parem {HTMLElement}   editorContainer   The editor element (which will be the container)
         * @returns {Object}
         */
        getFontData(elements, editorContainer) {
            Preconditions.isArray(elements);

            let fontData = {};

            for (let i in elements) {
                const elementWithFontData = DomHandler.getParentWithFontFamily(elements[i], editorContainer);
                const fontFamily = elementWithFontData.getAttribute("data-font-family");
                const fontProvider = elementWithFontData.getAttribute("data-font-provider");


                if (!fontData.fontFamily) {
                    fontData = {
                        fontFamily,
                        fontProvider,
                        fontMetadata: {
                            cssFamilyNames: elementWithFontData.style.fontFamily.split(",")
                        }
                    };

                    // Typekit
                    const kitID = elementWithFontData.getAttribute("data-kitid");
                    // Fonts.com
                    const projectId = elementWithFontData.getAttribute("data-projectid");
                    // Custom Fonts
                    const path = elementWithFontData.getAttribute("data-path");
                    const customFontId = elementWithFontData.getAttribute("data-customfontid");
                    const format = elementWithFontData.getAttribute("data-format");


                    if (kitID) {
                        fontData.fontMetadata.kitID = kitID;
                    } else if (projectId) {
                        fontData.fontMetadata.projectId = projectId;
                    } else if (path && customFontId && format) {
                        extend(fontData.fontMetadata, {path, customFontId, format});
                    }
                }

                if (fontData.fontFamily !== fontFamily) {
                    return {
                        fontFamily: null,
                        fontProvider: null,
                        fontMetadata: {}
                    };
                }
            }


            return fontData;
        }
    };

    return TextEditorViewHelpers;
});



// WEBPACK FOOTER //
// ./src/editor/ui/experience/widgets/textWidget/textEditor/TextEditorViewHelpers.js